import React, { KeyboardEvent, ReactElement, SyntheticEvent, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { EmojiPlugin } from "@draft-js-plugins/emoji";
import { localize } from "src/l10n";
import { addRecentEmoji, getRecentEmojiFromImage, IRecentEmoji, readRecentEmojis, saveRecentEmojis } from "./recentEmojis";

interface IProps {
    emojiPlugin: EmojiPlugin;
    /** Inserts a recent emoji where the cursor was. */
    onInsertEmoji: (unicode: string) => void;
    /** The picker closed: the focus goes back to the text. */
    onClosed: () => void;
}

const entrySelector = ".composer-emoji-entry";

/**
 * "Infoga emoji" (AMR-414, B37): the emoji plugin's own picker with the facelift's look, a name,
 * Esc and arrow keys, and "Senast använda" above the plugin's list (the plugin takes no group
 * of its own).
 */
function Visage2ComposerEmojiSelect(props: IProps): ReactElement {
    const { EmojiSelect } = props.emojiPlugin;
    const rootRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [popover, setPopover] = useState<HTMLElement | null>(null);
    const [recentEmojis, setRecentEmojis] = useState<IRecentEmoji[]>(readRecentEmojis);

    const getButton = (): HTMLButtonElement | null =>
        rootRef.current?.querySelector<HTMLButtonElement>(".composer-emoji-button") || null;

    // The plugin's button has no name or state of its own
    useEffect(() => {
        const button = getButton();

        if (!button) {
            return;
        }

        button.setAttribute("aria-label", localize("InfogaEmoji"));
        button.setAttribute("title", localize("InfogaEmoji"));
        button.setAttribute("aria-haspopup", "dialog");
        button.setAttribute("aria-expanded", isOpen ? "true" : "false");
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            setPopover(null);
            return;
        }

        const element = rootRef.current?.querySelector<HTMLElement>(".composer-emoji-popover");

        setPopover(element || null);
        element?.setAttribute("role", "dialog");
        element?.setAttribute("aria-label", localize("InfogaEmoji"));

        // Into the list, so the arrow keys work right away
        setTimeout(() => {
            element?.querySelector<HTMLButtonElement>(entrySelector)?.focus();
        }, 0);
    }, [isOpen]);

    const close = () => {
        // The plugin closes its popover on its own button
        getButton()?.click();
    };

    const remember = (emoji: IRecentEmoji | undefined) => {
        if (!emoji) {
            return;
        }

        const recent = addRecentEmoji(readRecentEmojis(), emoji);

        saveRecentEmojis(recent);
        setRecentEmojis(recent);
    };

    // A pick in the plugin's list: remember it before the plugin inserts it and closes
    const onPick = (event: SyntheticEvent) => {
        const entry = (event.target as HTMLElement).closest<HTMLElement>(entrySelector);

        if (!entry || entry.closest(".composer-emoji-recent")) {
            return;
        }

        // A long press opened the skin tones: the pick is the tone, not this
        if (!entry.closest(".composer-emoji-toneSelect") && popover?.querySelector(".composer-emoji-toneSelect")) {
            return;
        }

        if (event.type === "keydown") {
            if (!["Enter", " "].includes((event as KeyboardEvent).key)) {
                return;
            }

            // The plugin picks on keydown and puts the focus back in the text: the key itself
            // must not reach the text as a line break or a space
            event.preventDefault();
        }

        remember(getRecentEmojiFromImage(entry.querySelector("img")));
    };

    const moveFocus = (event: KeyboardEvent<HTMLDivElement>) => {
        const current = (event.target as HTMLElement).closest<HTMLElement>(entrySelector);

        if (!current || !popover) {
            return;
        }

        const entries = Array.from(popover.querySelectorAll<HTMLElement>(entrySelector))
            .filter((entry) => !entry.closest(".composer-emoji-toneSelect"));
        const index = entries.indexOf(current);
        let next: HTMLElement | undefined;

        if (event.key === "ArrowRight") {
            next = entries[index + 1];
        } else if (event.key === "ArrowLeft") {
            next = entries[index - 1];
        } else {
            // Up and down by position: the groups' rows don't line up in eights
            const from = current.getBoundingClientRect();
            const down = event.key === "ArrowDown";
            const rows = entries
                .map((entry) => ({ entry, rect: entry.getBoundingClientRect() }))
                .filter(({ rect }) => down ? rect.top > from.top + 4 : rect.top < from.top - 4);

            if (rows.length > 0) {
                const rowTop = down
                    ? Math.min(...rows.map(({ rect }) => rect.top))
                    : Math.max(...rows.map(({ rect }) => rect.top));
                const row = rows.filter(({ rect }) => Math.abs(rect.top - rowTop) < 4);

                next = row.reduce((best, item) =>
                    Math.abs(item.rect.left - from.left) < Math.abs(best.rect.left - from.left) ? item : best).entry;
            }
        }

        if (next) {
            event.preventDefault();
            next.focus();
            next.scrollIntoView({ block: "nearest" });
        }
    };

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (!isOpen) {
            return;
        }

        if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            close();
            return;
        }

        if (["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp"].includes(event.key)) {
            moveFocus(event);
        }
    };

    return (
        <div
            className="composer-emoji-select"
            ref={rootRef}
            onMouseUpCapture={onPick}
            onKeyDownCapture={onPick}
            onKeyDown={onKeyDown}>
            <EmojiSelect
                closeOnEmojiSelect
                onOpen={() => setIsOpen(true)}
                onClose={() => {
                    setIsOpen(false);
                    props.onClosed();
                }} />
            {popover && recentEmojis.length > 0 && createPortal(
                <div className="composer-emoji-recent">
                    <div className="composer-emoji-groupTitle">
                        {localize("SenastAnvanda")}
                    </div>
                    <ul className="composer-emoji-list">
                        {recentEmojis.map((emoji) => (
                            <li key={emoji.unicode} className="composer-emoji-item">
                                <button
                                    type="button"
                                    className="composer-emoji-entry"
                                    title={emoji.shortname}
                                    onClick={() => {
                                        remember(emoji);
                                        props.onInsertEmoji(emoji.unicode);
                                        close();
                                    }}>
                                    <img src={emoji.imageUrl} alt={emoji.unicode} draggable={false} />
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>,
                popover,
            )}
        </div>
    );
}

export default Visage2ComposerEmojiSelect;
